import * as React from "react";
import { Link } from "react-router";
import { ApiTokensPanel, DisplayTimeZonePanel, PageHeader, PasswordAndTwoFactor, SessionsPanel } from "@ozpanel/kit";

/**
 * The administrator's OWN security.
 *
 * The admin console had neither of these while the customer and mailbox consoles had both — so
 * the one account on this platform that can evict a node, read every tenant and mint credentials
 * could not see its own sessions or turn on a second factor. `/auth/session` has carried
 * `two_factor` and `/auth/sessions` has listed them since they were written; nothing here called
 * either. Standing rule 8, on the highest-privilege account there is.
 *
 * The panels come from @ozpanel/kit rather than being written again: these endpoints act on the
 * CALLER, so they are identical for every role, and a third copy is a third place to forget that
 * the session list reads `last_seen_at` and not `last_seen`.
 *
 * API tokens ARE here, and scoped the way the API actually is: /auth/tokens binds a token to
 * p.UserID — the caller — so this panel lists the administrator's OWN keys, and creates them. The
 * platform-wide view is a different question with a different blast radius, so it is its own screen
 * and its own endpoint: API tokens (/api-tokens, GET /auth/tokens/platform — every token of every
 * login, grouped by owner, revocable by the operator). This screen links to it rather than widening.
 *
 * The panel shows what each token has been USED for, not just that it is live: last_used_at answers
 * "is this current" and never "what has this been doing", which is the question that decides whether
 * to revoke one. That history comes from the audit log and outlives the token, because tokens are
 * hard-deleted and it is the only trail a revoked credential leaves.
 */
export function SecurityScreen() {
  return (
    <div className="space-y-4">
      <PageHeader
        title="Your security"
        subtitle="This is your own administrator account — not the platform's users. Changing your password signs out every other session."
      />
      <PasswordAndTwoFactor />
      <DisplayTimeZonePanel />
      <SessionsPanel />
      <div className="text-[12.5px] text-muted">
        Your own tokens are below; every token on the platform, by owner:{" "}
        <Link to="/api-tokens" className="text-accent hover:underline">API tokens</Link>.
      </div>
      <ApiTokensPanel />
    </div>
  );
}
